import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { 
  loginUser as apiLogin, 
  registerUser as apiRegister, 
  logoutUser as apiLogout, 
  verifyMFA as apiVerifyMFA 
} from '../api/auth.api';
import { getCurrentUser } from '../api/user.api';
import apiClient from '../api/apiClient';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [learningData, setLearningData] = useState(null);

  // FIXED: Use consistent token key across the app
  const TOKEN_KEY = 'authToken'; // Changed from 'auth_token' to match ApiService

  const setAuthToken = (token) => {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      localStorage.removeItem(TOKEN_KEY);
      delete apiClient.defaults.headers.common['Authorization'];
    }
  };

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      try {
        setAuthToken(token);
        const user = await getCurrentUser();
        if (user) {
          setCurrentUser(user);
          // Support both flat and nested user shapes
          const learningSource = user.learning_data || user.data?.learning_data;
          setLearningData(learningSource || null);
          setIsAuthenticated(true);
        } else {
          setAuthToken(null);
        }
      } catch (error) {
        console.error('Authentication check failed:', error);
        setAuthToken(null);
      }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  /**
   * Get browser geolocation data (optional, user may deny permission)
   * @returns {Promise<Object|null>} Location data or null if unavailable/denied
   */
  const getBrowserLocation = () => {
    return new Promise((resolve) => {
      // Check if geolocation is available
      if (!navigator.geolocation) {
        console.log('📍 Geolocation not supported by browser');
        // Still return timezone as fallback
        resolve({
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        });
        return;
      }

      // Try to get current position with timeout
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const locationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          };
          console.log('✅ Browser geolocation obtained:', locationData);
          resolve(locationData);
        },
        (error) => {
          // User denied permission or error occurred
          console.log('⚠️ Browser geolocation unavailable:', error.message);
          // Still return timezone as fallback
          resolve({
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            error: error.message
          });
        },
        {
          timeout: 5000, // 5 second timeout
          enableHighAccuracy: false, // Use less accurate but faster method
          maximumAge: 60000 // Accept cached location up to 1 minute old
        }
      );
    });
  };

  const login = async (email, password, mfaToken = null) => {
    try {
      // Get browser location data (non-blocking)
      const locationPromise = getBrowserLocation();
      let locationData = null;
      try {
        locationData = await Promise.race([
          locationPromise,
          new Promise(resolve => setTimeout(() => resolve(null), 2000))
        ]);
      } catch (error) {
        console.log('Location fetch error (non-critical):', error);
      }

      // Use the standardized loginUser API which uses apiClient
      const data = await apiLogin(email, password, mfaToken, locationData);

      if (data.success) {
        if (data.requiresMFA) {
          return {
            success: true,
            requiresMFA: true,
            userId: data.userId,
            message: data.message
          };
        } else {
          setAuthToken(data.token);
          setCurrentUser(data.user);
          setLearningData(data.user.learning_data || null);
          setIsAuthenticated(true);
          return { success: true };
        }
      } else {
        return {
          success: false,
          message: data.message || 'Login failed. Please check your credentials.'
        };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Network error. Please try again.'
      };
    }
  };

  const verifyMFA = async (userId, mfaToken) => {
    try {
      // 1. Verify the MFA token
      const data = await apiVerifyMFA(userId, mfaToken);

      if (data.success) {
        // 2. Obtain location data for the final login completion
        let locationData = null;
        try {
          locationData = await Promise.race([
            getBrowserLocation(),
            new Promise(resolve => setTimeout(() => resolve(null), 2000))
          ]);
        } catch (error) {
          console.log('Location fetch error during MFA:', error);
        }

        // 3. Complete the login process using loginUser
        const loginData = await apiLogin(currentUser?.email, '', mfaToken, locationData);

        if (loginData.success) {
          setAuthToken(loginData.token);
          setCurrentUser(loginData.user);
          setLearningData(loginData.user.learning_data || null);
          setIsAuthenticated(true);
          return { success: true };
        }
      }

      return {
        success: false,
        message: data.message || 'MFA verification failed.'
      };
    } catch (error) {
      console.error('MFA verification failed:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Network error. Please try again.'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await apiRegister(userData);
      // If registration returns a token, use it directly
      //  if (response.success && response.token) {
      //   setAuthToken(response.token);
      //   setCurrentUser(response.user);
      //   setLearningData(response.user.learning_data || null);

      // Check if user is active - if not, don't set authenticated
      // if (response.user && response.user.active === false) {
      //   // Store token but don't set authenticated
      //   return { 
      //     success: true, 
      //     message: response.message || 'Registration successful. 
      //     Your account is pending activation.'
      //   };
      // }
      // setIsAuthenticated(true);
      // return { success: true };

      // Registration successful - do NOT authenticate user
      // User must login after admin activates their account
      if (response.success) {
        return {
          success: true,
          message: response.message || 'Registration successful. Your account is pending activation. You will be able to login once an administrator activates your account.',
          requiresActivation: true
        };
      }

      // Don't try to login if registration was successful but account 
      // is inactive
      // if (response.success && response.message && response.message.
      // includes('pending activation')) {
      //   return { 
      //     success: true, 
      //     message: response.message 
      //   };
      // }

      // Fallback to login if no token returned
      // return await login(userData.user_data.email, userData.user_data.
      // password);

      return {
        success: false,
        message: response.message || 'Registration failed. Please try again.'
      };
    } catch (error) {
      console.error('Registration failed:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed. Please try again.'
      };
    }
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setCurrentUser(null);
      setLearningData(null);
      setIsAuthenticated(false);
      setAuthToken(null);
    }
  };

  const getDashboardData = () => {
    if (!learningData) return null;

    return {
      hasAssessment: learningData.has_assessment,
      competencyLevel: learningData.competency_level,
      learningJourney: learningData.learning_journey || [],
      topicProgress: learningData.topic_progress || {},
      overallProgress: learningData.overall_progress_percentage || 0,
      nextRecommendedTopic: learningData.next_recommended_topic,
      currentScenario: learningData.current_scenario,
      level: learningData.level || 1,
      xp: learningData.xp || 0,
      streakDays: learningData.streak_days || 0,
      strengths: learningData.strengths || [],
      weaknesses: learningData.weaknesses || []
    };
  };

  /**
   * Mark that the user has accepted data security & privacy policies.
   * This updates both flat and nested shapes of currentUser used across the app.
   */
  const markPoliciesAccepted = () => {
    setCurrentUser((prev) => {
      if (!prev) return prev;

      // If shape is flat user object
      const hasFlat = Object.prototype.hasOwnProperty.call(prev, 'hasAcceptedPolicies');
      const nextFlat = hasFlat
        ? { ...prev, hasAcceptedPolicies: true }
        : prev;

      // If shape is { success, data: { ...user } }
      if (prev.data && typeof prev.data === 'object') {
        return {
          ...nextFlat,
          data: {
            ...prev.data,
            hasAcceptedPolicies: true
          }
        };
      }

      return nextFlat;
    });
  };

  const value = {
    currentUser,
    isAuthenticated,
    isLoading,
    login,
    logout,
    register,
    verifyMFA,
    learningData, // Added missing learningData to context value
    getDashboardData, // Added missing getDashboardData to context value
    markPoliciesAccepted
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};



export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};